import BroadBandPackage, {
    BroadBandPackageName,
} from '@/modules/Broadband/models/BroadBandPackage';
import ABTest, { theLiveABTest } from '../models/ab-testing';
import {
    STANDARD_PRICES,
    STREAMING_CAMPAIGN_PRODUCT_TITLES,
} from '../utilities/settings';
import { campaign_types, ICampaign, product_type } from './campaign-shared';
import { BBInfoItem } from './sows';
import { th } from '@smooth-ui/core-sc';

export default class InternetCampaign implements ICampaign {
    product_type: product_type = 'internet';
    campaign_type: campaign_types;
    product: BBInfoItem | null;
    abTest: ABTest;

    constructor(
        campaign_type: campaign_types,
        product: BBInfoItem | null,
        abTest?: ABTest // Provide a mock or we default to live instance
    ) {
        this.campaign_type = campaign_type;
        this.product = product;
        this.abTest = abTest || theLiveABTest;
    }

    getNumberOfCampaignMonths(): number | undefined {
        if (this.product) {
            if (this.campaign_type === 'fibia-aarhus') {
                return 6;
            } else if (this.campaign_type === 'april2023InternetTrim') {
                if (
                    // Maxi
                    (this.product.down_speed === 300 &&
                        this.product.technology_type === 'fiber') ||
                    this.product.down_speed === 500
                ) {
                    return 5;
                } else if (
                    // Mini, coax
                    this.product.down_speed === 100 &&
                    this.product.technology_type === 'coax'
                ) {
                    return 5;
                } else {
                    return undefined;
                }
            } else if (this.campaign_type === 'studiekampagne2021') {
                return 12;
            } else if (this.campaign_type === 'in_store_feb19') {
                return 2;
            } else if (this.campaign_type === 'in_store_1month_free') {
                return 1;
            } else if (this.campaign_type === 'in_store_3month_free') {
                return 3;
            } else if (this.campaign_type === '2021wifigaranti') {
                const broadBandPackage = new BroadBandPackage(this.product);
                if (broadBandPackage.name === BroadBandPackageName.giga) {
                    return 1;
                } else {
                    return undefined;
                }
            } else if (this.campaign_type === 'may2021wifigaranti') {
                const broadBandPackage = new BroadBandPackage(this.product);
                if (broadBandPackage.name === BroadBandPackageName.giga) {
                    return 1;
                } else if (
                    this.product.technology_type === 'fiber' &&
                    (broadBandPackage.name === BroadBandPackageName.maxi ||
                        broadBandPackage.name === BroadBandPackageName.midi)
                ) {
                    return 1;
                } else {
                    return undefined;
                }
            } else {
                return undefined;
            }
        } else {
            return undefined;
        }
    }

    getNumberOfBindingMonths() {
        if (
            this.campaign_type === 'studiekampagne2021' ||
            this.campaign_type === '2021wifigaranti' ||
            this.campaign_type === 'may2021wifigaranti'
        ) {
            return 1;
        } else {
            return 6;
        }
    }

    hasFeeLines(): boolean {
        return (
            !!this.product && !!this.product.lines && !!this.product.lines.fees
        );
    }

    getFeeLines(): any[] {
        return this.hasFeeLines() && this.product && this.product.lines.fees
            ? this.product.lines.fees
            : [];
    }

    getInitialFee(): number {
        if (!this.product) {
            return 0;
        } else {
            if (this.campaign_type === 'may2021wifigaranti') {
                const broadBandPackage = new BroadBandPackage(this.product);

                if (broadBandPackage.name === BroadBandPackageName.giga) {
                    return 0;
                } else if (
                    this.product.technology_type === 'fiber' &&
                    (broadBandPackage.name === BroadBandPackageName.maxi ||
                        broadBandPackage.name === BroadBandPackageName.midi)
                ) {
                    return 0;
                } else if (
                    this.product.technology_type === 'coax' &&
                    broadBandPackage.name === BroadBandPackageName.maxi
                ) {
                    return 0;
                } else {
                    return STANDARD_PRICES.initial_fee;
                }
            } else if (this.campaign_type === '2021wifigaranti') {
                const broadBandPackage = new BroadBandPackage(this.product);

                if (
                    broadBandPackage.name === BroadBandPackageName.maxi ||
                    broadBandPackage.name === BroadBandPackageName.giga
                ) {
                    return 0;
                } else {
                    return STANDARD_PRICES.initial_fee;
                }
            } else if (this.campaign_type === 'in_store_feb19') {
                if (this.product.down_speed >= 200) {
                    return 0;
                } else {
                    return STANDARD_PRICES.initial_fee;
                }
            } else {
                const feeLine = this.product.lines.fees?.find(
                    (line) => line.name === 'Oprettelsesgebyr'
                );
                return feeLine
                    ? parseInt(feeLine.price.initial, 10)
                    : STANDARD_PRICES.initial_fee;
            }
        }
    }

    getShipmentFee(): number {
        return 0;
    }

    getTotalInitialPrice(): number {
        return (
            this.getInitialFee() + this.getShipmentFee() + this.getRecurrent()
        );
    }

    getTotalRecurrentInBindingPeriod(): number {
        const noOfCampaignMonths = this.getNumberOfCampaignMonths() || 0;
        if (
            this.campaign_type === 'april2023InternetTrim' ||
            this.campaign_type === 'in_store_feb19' ||
            this.campaign_type === 'in_store_1month_free' ||
            this.campaign_type === 'in_store_3month_free'
        ) {
            const noOfFullPriceMonths = !!noOfCampaignMonths
                ? Math.max(
                      this.getNumberOfBindingMonths() - noOfCampaignMonths,
                      0
                  )
                : this.getNumberOfBindingMonths();

            let recurrentPriceOfAddons = 0;

            if (
                this.product &&
                this.product.addons &&
                this.product.addons.addons
            ) {
                recurrentPriceOfAddons =
                    this.product.addons.addons.reduce((acc, addon) => {
                        if (
                            this.campaign_type === '2022internetStreamingKombi'
                        ) {
                            if (
                                addon.name.indexOf(
                                    STREAMING_CAMPAIGN_PRODUCT_TITLES.TV2PLAY
                                ) === -1 &&
                                addon.name.indexOf(
                                    STREAMING_CAMPAIGN_PRODUCT_TITLES.HBOMAX
                                ) === -1
                            ) {
                                return acc + addon.price.recurrent;
                            } else {
                                return acc;
                            }
                        } else {
                            return acc + addon.price.recurrent;
                        }
                    }, 0) * this.getNumberOfBindingMonths();
            }

            const campaignPrice = this.getRecurrent();
            const fullPrice = this.product?.price.recurrent;

            return (
                noOfFullPriceMonths * (fullPrice || 0) +
                campaignPrice * noOfCampaignMonths +
                recurrentPriceOfAddons
            );
        } else {
            return (
                this.getRecurrent({ includeAddons: true }) *
                this.getNumberOfBindingMonths()
            );
        }
    }

    getTotalPriceInBindingPeriod(): number {
        if (
            this.campaign_type === 'april2023InternetTrim' ||
            this.campaign_type === 'fibia-aarhus'
        ) {
            return (
                this.getInitialFee() +
                this.getShipmentFee() +
                this.getTotalRecurrentInBindingPeriod() +
                STANDARD_PRICES.administration_quarterly * 2
            );
        } else {
            return (
                this.getInitialFee() +
                this.getShipmentFee() +
                this.getTotalRecurrentInBindingPeriod()
            );
        }
    }

    getRecurrent(config?: { includeAddons?: boolean }): number {
        const default_recurrent =
            this.product && this.product.price && this.product.price.recurrent
                ? this.product.price.recurrent
                : 0;

        if (this.product) {
            let price = default_recurrent;
            let recurrentPriceOfAddons = 0;

            if (config && config.includeAddons === true) {
                if (this.product.addons && this.product.addons.addons) {
                    recurrentPriceOfAddons = this.product.addons.addons.reduce(
                        (acc, addon) => {
                            return acc + addon.price.recurrent;
                        },
                        0
                    );
                }
            }

            if (this.campaign_type === 'fibia-aarhus') {
                if (this.product.down_speed === 1000) {
                    return 199;
                }
            }

            if (this.campaign_type === 'april2023InternetTrim') {
                if (this.product.down_speed === 500) {
                    price = 100;
                } else if (
                    this.product.down_speed === 300 &&
                    this.product.technology_type === 'fiber'
                ) {
                    price = 100;
                } else if (
                    this.product.down_speed === 100 &&
                    this.product.technology_type === 'coax'
                ) {
                    price = 100;
                }
            } else if (this.campaign_type === 'studiekampagne2021') {
                price = this.product.price.recurrent - 50;
            } else if (
                this.campaign_type === 'in_store_feb19' ||
                this.campaign_type === 'in_store_1month_free' ||
                this.campaign_type === 'in_store_3month_free'
            ) {
                price = 0;
            } else if (this.campaign_type === '2021wifigaranti') {
                if (this.product.down_speed === 1000) {
                    price = 0;
                } else {
                    price = this.product.price.recurrent;
                }
            } else if (this.campaign_type === 'may2021wifigaranti') {
                const pack = new BroadBandPackage(this.product);
                if (pack.name === BroadBandPackageName.giga) {
                    price = 0;
                } else if (
                    this.product.technology_type === 'fiber' &&
                    (pack.name === BroadBandPackageName.maxi ||
                        pack.name === BroadBandPackageName.midi)
                ) {
                    price = 0;
                } else {
                    price = this.product.price.recurrent;
                }
            }

            return price + recurrentPriceOfAddons;
        } else {
            return default_recurrent;
        }
    }

    getPreviousRecurrent(): number | undefined {
        if (this.product) {
            if (this.campaign_type === 'fibia-aarhus') {
                if (this.product.down_speed === 1000) {
                    return this.product.price.recurrent;
                } else {
                    return undefined;
                }
            } else if (this.campaign_type === 'april2023InternetTrim') {
                if (this.product.down_speed === 500) {
                    return this.product.price.recurrent;
                } else if (
                    this.product.down_speed === 300 &&
                    this.product.technology_type === 'fiber'
                ) {
                    return this.product.price.recurrent;
                } else if (
                    this.product.down_speed === 100 &&
                    this.product.technology_type === 'coax'
                ) {
                    return this.product.price.recurrent;
                } else {
                    return undefined;
                }
            } else if (this.campaign_type === 'studiekampagne2021') {
                return this.product.price.recurrent;
            } else if (
                (this.campaign_type === 'in_store_feb19' ||
                    this.campaign_type === 'in_store_1month_free' ||
                    this.campaign_type === 'in_store_3month_free') &&
                this.product.price.recurrent
            ) {
                return this.product.price.recurrent;
            } else if (this.campaign_type === '2021wifigaranti') {
                if (this.product.down_speed === 1000) {
                    return this.product.price.recurrent;
                } else {
                    return undefined;
                }
            } else if (this.campaign_type === 'may2021wifigaranti') {
                const pack = new BroadBandPackage(this.product);
                if (pack.name === BroadBandPackageName.giga) {
                    return this.product.price.recurrent;
                } else if (
                    this.product.technology_type === 'fiber' &&
                    (pack.name === BroadBandPackageName.maxi ||
                        pack.name === BroadBandPackageName.midi)
                ) {
                    return this.product.price.recurrent;
                } else {
                    return undefined;
                }
            } else {
                return undefined;
            }
        } else {
            return undefined;
        }
    }

    /**
     * This method should only be used as a last resort.
     * It's absolutely preferable to have the campaign codes passed through transparently from the product
     * data itself – not added by frontend code. History shows it creates nasty complexity.
     * So: defaultValue is what we want from this method!
     */
    getOfferCode(): string {
        const defaultValue = this.product ? this.product.offer_code : '';

        if (this.campaign_type === null) {
            return defaultValue;
        } else {
            if (this.product) {
                if (
                    this.campaign_type === 'fibia-aarhus' &&
                    this.product.down_speed === 1000
                ) {
                    return 'fibiaaarhus23';
                }
                if (this.campaign_type === 'april2023InternetTrim') {
                    if (
                        this.product.down_speed === 300 ||
                        this.product.down_speed === 500
                    ) {
                        if (this.product.technology_type === 'fiber') {
                            if (this.product.infrastructure_owner === 'eniig') {
                                return 'E-IN800000';
                            } else {
                                return '500_TIL_100_5_MDR';
                            }
                        } else if (
                            this.product.technology_type === 'coax' &&
                            this.product.down_speed === 500
                        ) {
                            const listepris = 309;
                            const foreningspris = 289;

                            if (this.product.price.recurrent === listepris) {
                                return '1152';
                            } else if (
                                this.product.price.recurrent === foreningspris
                            ) {
                                return '1153';
                            } else {
                                return ''; // Free shipping for ccb
                            }
                        } else {
                            return '';
                        }
                    } else if (this.product.down_speed === 100) {
                        if (this.product.technology_type === 'coax') {
                            const listepris = 259;
                            const foreningspris = 239;

                            if (this.product.price.recurrent === listepris) {
                                return '1150';
                            } else if (
                                this.product.price.recurrent === foreningspris
                            ) {
                                return '1151';
                            } else {
                                return '';
                            }
                        } else {
                            return '';
                        }
                    } else {
                        if (this.product.technology_type === 'coax') {
                            return '169';
                        } else {
                            return '';
                        }
                    }
                } else if (this.campaign_type === 'studiekampagne2021') {
                    if (this.product.technology_type === 'fiber') {
                        return 'BB_SAVE_50KR_12MDR';
                    } else if (this.product.technology_type === 'coax') {
                        return '1060';
                    } else {
                        return defaultValue;
                    }
                } else if (this.campaign_type === 'in_store_feb19') {
                    if (this.product.technology_type === 'coax') {
                        return '1075';
                    } else if (this.product.technology_type === 'fiber') {
                        return 'BB_2MDR_UB';
                    } else {
                        return defaultValue;
                    }
                } else if (this.campaign_type === 'in_store_1month_free') {
                    if (this.product.technology_type === 'coax') {
                        return '1051';
                    } else if (this.product.technology_type === 'fiber') {
                        return 'BB_1MDR_UB';
                    } else {
                        return defaultValue;
                    }
                } else if (this.campaign_type === 'in_store_3month_free') {
                    if (this.product.technology_type === 'coax') {
                        return '1069';
                    } else if (this.product.technology_type === 'fiber') {
                        return 'BB_3MDR_UB';
                    } else {
                        return defaultValue;
                    }
                } else {
                    return defaultValue;
                }
            } else {
                return defaultValue;
            }
        }
    }
}
